import { useCallback, useEffect } from 'react';
import { type AgentDecisionEvent, useWebSocketEvents } from '@/hooks/useWebSocketEvents';
import { type AgentDecision, useAgentStore } from '@/stores/agentStore';
import { historyService } from '@/services/history';
import { toast } from '@/hooks/useToast';

function wsEventToDecision(event: AgentDecisionEvent): AgentDecision {
  return {
    id: event.decision_id,
    // Fall back to 'hold' so a malformed WS event never produces an entry
    // that crashes the Approvals page when it renders `.toUpperCase()`.
    decisionType: (event.decision_type ?? 'hold') as AgentDecision['decisionType'],
    symbol: event.symbol ?? '',
    quantity: event.quantity ?? null,
    price: event.price ?? null,
    notional: event.notional ?? null,
    reasoning: event.reasoning,
    confidence: event.confidence ?? 0,
    outcome: (event.outcome ?? 'pending') as AgentDecision['outcome'],
    createdAt: new Date().toISOString(),
  };
}

export function AgentApprovalManager() {
  const { addPendingApproval, addDecision } = useAgentStore();

  // Fetch any existing awaiting_approval decisions on mount. This widget is
  // mounted globally by MainLayout (every authenticated page), so its 401 must
  // NOT trip the app-wide onUnauthorized hard-logout — `suppressUnauthorized`
  // keeps a failed background poll silent; approvals still arrive via WebSocket.
  useEffect(() => {
    const fetchPendingDecisions = async () => {
      try {
        const data = await historyService.getAgentDecisions(
          { outcome: 'awaiting_approval', pageSize: 10 },
          { suppressUnauthorized: true },
        );
        for (const decision of data.decisions) {
          addPendingApproval(decision);
        }
      } catch {
        // Non-fatal: approvals will still come via WebSocket
      }
    };
    fetchPendingDecisions();
  }, [addPendingApproval]);

  const handleAgentDecision = useCallback(
    (event: AgentDecisionEvent) => {
      const decision = wsEventToDecision(event);
      addDecision(decision);

      if (event.awaiting_approval) {
        addPendingApproval(decision);
        toast({
          title: 'Agent Decision Pending',
          description: `${decision.decisionType.toUpperCase()} ${decision.symbol} needs approval`,
          variant: 'default',
          duration: 10000,
        });
      }
    },
    [addDecision, addPendingApproval]
  );

  useWebSocketEvents({ onAgentDecision: handleAgentDecision });

  return null;
}
